import { Dispatch, SetStateAction, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useWindowSize } from "@utils/hooks";
import { Membership, QtyRules } from "../types";
import { MobileSelect } from "@components/dropdowns";
import LoadingSpinner from "@components/loadingSpinner";
import { Button, Text } from "@components/theming";
import { TextInput, Select } from "@components/inputs";
import { useIsShopsite } from "@context/DomainContext";

interface Props {
  qtyRules: QtyRules;
  memberShips: Membership;
  membershipDiscount: Record<number, Membership>;
  setMembershipDiscount: Dispatch<SetStateAction<Record<number, Membership>>>;
  handleDiscount: (
    discountId: number,
    isRetro: boolean,
    paQty: string
  ) => Promise<void>;
  discountId: number;
  isRetro: boolean;
  loading: boolean;
}

const DiscountMembershipDropdown = ({
  discountId,
  memberShips,
  membershipDiscount,
  handleDiscount,
  setMembershipDiscount,
  qtyRules,
  isRetro,
  loading,
}: Props) => {
  const isShopsite = useIsShopsite();
  const [selectedNum, setSelectedNum] = useState(qtyRules.Max);
  const windowWidth = useWindowSize().width;

  const t = useTranslations("Checkout.Cart.tickets.discount");

  return (
    <div className="flex flex-col gap-y-2 w-full">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-6">
        <div className="sm:col-span-3">
          <Text
            as="label"
            htmlFor={memberShips.UsernameLabel}
            sharedClasses="block text-sm font-medium leading-6"
          >
            {memberShips.UsernameLabel}
          </Text>
          <div className="mt-2">
            <TextInput
              type="text"
              name={memberShips.UsernameLabel}
              id={memberShips.UsernameLabel}
              placeholder={memberShips.UsernameLabel}
              onChange={(e) => {
                setMembershipDiscount((prev) => ({
                  ...prev,
                  ...{
                    [discountId]: {
                      ...prev[discountId],
                      UsernameLabel: e.target.value,
                    },
                  },
                }));
              }}
              sharedClasses="w-full"
              shopsiteClasses="text-sm !rounded"
            />
          </div>
        </div>
        <div className="sm:col-span-3">
          <Text
            as="label"
            htmlFor={memberShips.PasswordLabel}
            sharedClasses="block text-sm font-medium leading-6"
          >
            {memberShips.PasswordLabel}
          </Text>
          <div className="mt-2">
            <TextInput
              type="text"
              name={memberShips.PasswordLabel}
              id={memberShips.PasswordLabel}
              placeholder={memberShips.PasswordLabel}
              onChange={(e) => {
                setMembershipDiscount((prev) => ({
                  ...prev,
                  ...{
                    [discountId]: {
                      ...prev[discountId],
                      PasswordLabel: e.target.value,
                    },
                  },
                }));
              }}
              sharedClasses="w-full"
              shopsiteClasses="text-sm !rounded"
            />
          </div>
        </div>
        <div className="col-span-full flex items-center justify-between gap-x-6">
          <Text as="label" sharedClasses="block text-sm font-medium leading-6">
            {t("quantity")}
          </Text>
          <div>
            {windowWidth && windowWidth > 640 ? (
              <Select
                value={selectedNum.toString()}
                onValueChange={(value) => setSelectedNum(parseInt(value))}
                options={Array.from(
                  { length: qtyRules.Max - qtyRules.Min + 1 },
                  (_, index) => index + qtyRules.Min
                ).map((value) => ({
                  label: value.toString(),
                  value: value.toString(),
                }))}
                shopsiteButtonClasses="text-sm gap-2 px-4 focus:px-[15px] !rounded"
              />
            ) : (
              <MobileSelect
                options={Array.from(
                  { length: qtyRules.Max - qtyRules.Min + 1 },
                  (_, index) => index + qtyRules.Min
                ).map((v) => ({ label: v, value: v }))}
                onOptionSelect={(option) => setSelectedNum(parseInt(option))}
              >
                {(selected) => (
                  <Button
                    variant="surface"
                    shopsiteVariant="white"
                    color="gray"
                    whitelabelClasses="bg-[color:var(--color-surface)] text-[color:var(--gray-12)]"
                    shopsiteClasses="text-sm !rounded h-9 px-4"
                  >
                    <div className="inline-flex items-center justify-center gap-1">
                      {selected || qtyRules.Min}
                      <ChevronDownIcon className="w-4 h-4" />
                    </div>
                  </Button>
                )}
              </MobileSelect>
            )}
          </div>
        </div>
      </div>
      <Button
        type="button"
        highContrast
        disabled={
          loading ||
          membershipDiscount[discountId].PasswordLabel === "" ||
          membershipDiscount[discountId].UsernameLabel === ""
        }
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await handleDiscount(
            discountId,
            isRetro,
            `${qtyRules.Id}=${selectedNum}`
          );
        }}
        sharedClasses="w-full mt-2"
        shopsiteClasses="text-sm bg-indigo-600"
      >
        {loading ? (
          <LoadingSpinner
            size="20"
            color={isShopsite ? "#ffffff" : "var(--gray-11)"}
          />
        ) : (
          t("apply")
        )}
      </Button>
    </div>
  );
};

export default DiscountMembershipDropdown;
